import { InputButton, type NumberInputProps } from '@homestake/shared/ui';
import { observer, use$, useObserve } from '@legendapp/state/react';
import { Group } from '@mantine/core';
import { useForm } from '@tanstack/react-form';
import Decimal from 'decimal.js';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { z } from 'zod';
import { configurationState$, manualBetState$ } from '../../../state';
import { freeBetState$ } from '../../../state/free-bet.state';
import { BetAmountInput } from '../BetAmountInput';

export type ManualBetAmountNumberInputProps = NumberInputProps;

export const ManualBetAmountNumberInput = observer(
  (props: ManualBetAmountNumberInputProps) => {
    const isBetting = use$(manualBetState$.isBetting);
    const minBetAmount = use$(configurationState$.minBet);
    const maxBetAmount = use$(configurationState$.maxBet);
    const betAmount = use$(manualBetState$.betAmount);
    const isHalfAmountDisabled = betAmount === minBetAmount;
    const isDoubleAmountDisabled = betAmount === maxBetAmount;
    const isFreeBetting = use$(freeBetState$.isActive);

    const schema = useMemo(
      () =>
        z.object({
          betAmount: z.number().min(minBetAmount).max(maxBetAmount),
        }),
      [minBetAmount, maxBetAmount]
    );

    const { Field, setFieldValue } = useForm({
      validators: {
        onChange: schema,
      },
      defaultValues: {
        betAmount,
      } as z.infer<typeof schema>,
    });

    useObserve(manualBetState$.betAmount, ({ value }) => {
      setFieldValue('betAmount', value ?? 0);
    });

    const { t } = useTranslation();
    const handleHalfAmountClick = () => {
      const currentBetAmount = manualBetState$.betAmount.peek();
      manualBetState$.setBetAmount(
        new Decimal(currentBetAmount).div(2).toNumber()
      );
    };

    const handleDoubleAmountClick = () => {
      const currentBetAmount = manualBetState$.betAmount.peek();
      manualBetState$.setBetAmount(
        new Decimal(currentBetAmount).mul(2).toNumber()
      );
    };

    return (
      <Field
        listeners={{
          onChange: ({ value }) => {
            manualBetState$.setBetAmount(value);
          },
        }}
        name="betAmount"
      >
        {({ handleChange, state: { value } }) => (
          <BetAmountInput
            data-test-id="manual-bet-amount-input"
            disabled={isBetting || isFreeBetting}
            onChange={(newValue) => {
              handleChange(Number(newValue));
            }}
            rightSection={
              <Group gap={4}>
                <InputButton
                  data-test-id="manual-bet-amount-number-input-half-amount-button"
                  disabled={isBetting || isHalfAmountDisabled}
                  onClick={handleHalfAmountClick}
                >
                  {t('betAmountInput.half')}
                </InputButton>
                <InputButton
                  data-test-id="manual-bet-amount-number-input-double-amount-button"
                  disabled={isBetting || isDoubleAmountDisabled}
                  onClick={handleDoubleAmountClick}
                >
                  {t('betAmountInput.double')}
                </InputButton>
              </Group>
            }
            value={value}
            {...props}
          />
        )}
      </Field>
    );
  }
);
